const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Email address to receive feedback notifications
 */
const FEEDBACK_EMAIL = 'support@precisiontrader.tech';

/**
 * Service for submitting landing page feedback
 * 
 * Note: The backend endpoint should send an email notification to support@precisiontrader.tech
 * when feedback is received.
 */
class FeedbackService {
  /**
   * Submit feedback data to backend
   * @param {Object} data - Feedback data
   * @param {string} data.primaryReason - Selected primary reason
   * @param {string} [data.followUpAnswer] - Optional follow-up answer
   * @param {string} data.triggerReason - 'behavior' | 'exit_intent'
   * @param {string} [data.timestamp] - ISO timestamp (auto-generated if not provided)
   * @param {string} [data.pageUrl] - Current page URL (auto-detected if not provided)
   * @param {string} [data.userAgent] - User agent (auto-detected if not provided)
   * @returns {Promise<Object>} Response from server
   */
  async submitFeedback(data) {
    const payload = {
      primaryReason: data.primaryReason,
      triggerReason: data.triggerReason,
      timestamp: data.timestamp || new Date().toISOString(),
      pageUrl: data.pageUrl || window.location.href,
      userAgent: data.userAgent || navigator.userAgent,
      // Include email address for backend to send notification
      notificationEmail: FEEDBACK_EMAIL,
    };

    // Add follow-up answer if provided
    if (data.followUpAnswer) {
      payload.followUpAnswer = data.followUpAnswer;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Feedback submission failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Don't throw - we want to handle errors gracefully
      // The feedback was captured locally even if submission fails
      return { success: false, error: error.message };
    }
  }
}

export default new FeedbackService();
